/**
 * Integrations — API Keys + Webhooks management.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowRight,
  Bot,
  CheckCircle2,
  Copy,
  Globe,
  Key,
  Loader2,
  Plus,
  Send,
  Sparkles,
  Terminal,
  Trash2,
  Webhook,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  apiKeysApi,
  webhooksApi,
  type ApiKeyCreated,
  type WebhookCreated,
} from "@/lib/api-system";
import { formatDateTime, formatRelative } from "@/lib/format";

const WEBHOOK_EVENTS = [
  "job.created",
  "job.assigned",
  "job.completed",
  "job.approved",
  "invoice.created",
  "invoice.paid",
  "broadcast.dispatched",
];

export function IntegrationsPage() {
  return (
    <>
      <PageHeader
        title="Integrações"
        description="API keys e webhooks pra sistemas externos."
      />

      <Tabs defaultValue="mcp">
        <TabsList>
          <TabsTrigger value="mcp"><Bot className="h-4 w-4" /> MCP / Agents IA</TabsTrigger>
          <TabsTrigger value="api-keys"><Key className="h-4 w-4" /> API Keys</TabsTrigger>
          <TabsTrigger value="webhooks"><Webhook className="h-4 w-4" /> Webhooks</TabsTrigger>
        </TabsList>

        <TabsContent value="mcp">
          <McpSection />
        </TabsContent>
        <TabsContent value="api-keys">
          <ApiKeysSection />
        </TabsContent>
        <TabsContent value="webhooks">
          <WebhooksSection />
        </TabsContent>
      </Tabs>
    </>
  );
}

// ─── MCP / Agents IA ───────────────────────────────────────────────────────

function McpSection() {
  const SUPPORTED = [
    { name: "Claude Code", icon: Terminal, color: "from-amber-400 to-orange-500" },
    { name: "Claude Desktop", icon: Bot, color: "from-violet-500 to-purple-600" },
    { name: "Cursor", icon: Sparkles, color: "from-sky-500 to-blue-600" },
    { name: "Codex CLI", icon: Terminal, color: "from-emerald-500 to-teal-600" },
    { name: "OpenCode", icon: Sparkles, color: "from-rose-500 to-pink-600" },
    { name: "Windsurf", icon: Bot, color: "from-cyan-500 to-blue-600" },
    { name: "Zed", icon: Sparkles, color: "from-indigo-500 to-blue-700" },
  ];

  return (
    <div className="space-y-6">
      {/* Hero card */}
      <div className="relative overflow-hidden rounded-3xl border border-emerald-200/60 dark:border-emerald-500/20 bg-gradient-to-br from-emerald-50 via-white to-sky-50 dark:from-emerald-950/40 dark:via-card/40 dark:to-sky-950/40 p-8">
        <div className="absolute -top-12 -right-12 h-48 w-48 rounded-full bg-emerald-400/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-12 -left-12 h-48 w-48 rounded-full bg-sky-400/20 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="flex items-center gap-2">
            <Badge variant="success" dot>Novo</Badge>
            <Badge variant="purple">Model Context Protocol</Badge>
          </div>
          <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight">
            Conecte Claude, Cursor e outras IAs ao op-next
          </h2>
          <p className="mt-2 text-sm sm:text-base text-muted-foreground max-w-2xl">
            Servidor MCP oficial expõe <span className="font-semibold text-foreground">24 tools</span>,{" "}
            <span className="font-semibold text-foreground">3 resources</span> e{" "}
            <span className="font-semibold text-foreground">3 prompts</span> — agents podem criar chamados,
            atribuir técnicos, aprovar faturas, gerar relatórios e operar a plataforma direto do seu IDE/CLI.
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <Button asChild size="lg">
              <Link to="/mcp">
                Configurar agora <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            <a
              href="https://modelcontextprotocol.io"
              target="_blank"
              rel="noreferrer"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1"
            >
              Saiba mais sobre MCP <ArrowRight className="h-3 w-3" />
            </a>
          </div>
        </div>
      </div>

      {/* Agents grid */}
      <div>
        <h3 className="font-semibold text-sm mb-3">7 agents suportados oficialmente</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {SUPPORTED.map((a) => (
            <Link
              key={a.name}
              to="/mcp"
              className="group rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white/70 dark:bg-card/40 p-4 transition-all hover:-translate-y-0.5 hover:shadow-lg"
            >
              <div className={`mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ${a.color} shadow-lg`}>
                <a.icon className="h-5 w-5 text-white" />
              </div>
              <p className="font-semibold text-sm">{a.name}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">Configurar →</p>
            </Link>
          ))}
        </div>
      </div>

      {/* Feature highlights */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <FeatureBox
          icon={Terminal}
          title="24 tools prontas"
          description="CRUD + actions de jobs, clientes, técnicos, faturas, reports e notificações"
        />
        <FeatureBox
          icon={Sparkles}
          title="3 prompts pré-prontos"
          description="atender_chamado · fechamento_mensal · briefing_diario"
        />
        <FeatureBox
          icon={Key}
          title="Auth segura"
          description="API key isolada, scoped, revogável a qualquer momento"
        />
      </div>
    </div>
  );
}

function FeatureBox({
  icon: Icon,
  title,
  description,
}: {
  icon: typeof Bot;
  title: string;
  description: string;
}) {
  return (
    <div className="rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white/70 dark:bg-card/40 p-5">
      <div className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-primary-50 text-primary-600 dark:bg-primary-500/10 dark:text-primary-300 mb-3">
        <Icon className="h-4 w-4" />
      </div>
      <p className="font-semibold text-sm">{title}</p>
      <p className="text-xs text-muted-foreground mt-1">{description}</p>
    </div>
  );
}

// ─── API Keys ──────────────────────────────────────────────────────────────

function ApiKeysSection() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [newKey, setNewKey] = useState<ApiKeyCreated | null>(null);

  const list = useQuery({ queryKey: ["api-keys"], queryFn: () => apiKeysApi.list() });

  const create = useMutation({
    mutationFn: () => apiKeysApi.create({ name }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["api-keys"] });
      setNewKey(data);
      setName("");
      setOpen(false);
      toast.success("API key criada");
    },
    onError: (e: Error) => toast.error("Falha", { description: e.message }),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => apiKeysApi.revoke(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["api-keys"] });
      toast.success("Revogada");
    },
  });

  const del = useMutation({
    mutationFn: (id: string) => apiKeysApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["api-keys"] });
      toast.success("Removida");
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Tokens pra acesso server-to-server. O valor completo aparece só na criação.
        </p>
        <Button onClick={() => setOpen(true)}>
          <Plus className="h-4 w-4" /> Nova chave
        </Button>
      </div>

      {newKey && (
        <div className="rounded-2xl border-2 border-amber-300/60 dark:border-amber-500/30 bg-amber-50/40 dark:bg-amber-500/[0.04] p-5">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-sm">Salve esta chave agora — ela não será mostrada novamente</h4>
              <div className="mt-3 flex items-center gap-2">
                <code className="flex-1 rounded-lg bg-white dark:bg-black/30 border border-amber-300/60 p-2.5 font-mono text-xs break-all">
                  {newKey.raw_key}
                </code>
                <Button
                  size="icon-sm"
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(newKey.raw_key);
                    toast.success("Copiada");
                  }}
                >
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setNewKey(null)} className="mt-3">
                Fechar e esquecer
              </Button>
            </div>
          </div>
        </div>
      )}

      {list.data?.items.length === 0 ? (
        <EmptyState icon={Key} title="Sem API keys" description="Crie uma chave pra integrar sistemas externos." />
      ) : (
        <div className="rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white/70 dark:bg-card/40 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/80 dark:bg-white/[0.03]">
              <tr>
                <th className="text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground px-4 py-3">Nome</th>
                <th className="text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground px-4 py-3">Prefixo</th>
                <th className="text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground px-4 py-3">Status</th>
                <th className="text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground px-4 py-3">Último uso</th>
                <th className="text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {list.data?.items.map((k) => (
                <tr key={k.id} className="border-t border-slate-200/40 dark:border-white/5">
                  <td className="px-4 py-3 font-medium">{k.name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{k.prefix}…</td>
                  <td className="px-4 py-3">
                    {k.revoked_at ? <Badge variant="danger" dot>Revogada</Badge> : k.is_active ? <Badge variant="success" dot>Ativa</Badge> : <Badge variant="muted" dot>Inativa</Badge>}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{k.last_used_at ? formatRelative(k.last_used_at) : "—"}</td>
                  <td className="px-4 py-3 text-right">
                    {!k.revoked_at && (
                      <Button variant="ghost" size="sm" onClick={() => revoke.mutate(k.id)} className="text-amber-600">
                        Revogar
                      </Button>
                    )}
                    <Button variant="ghost" size="icon-sm" onClick={() => del.mutate(k.id)} className="text-rose-600">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Nova API key</SheetTitle>
          </SheetHeader>
          <SheetBody className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Nome da chave</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: Integração ERP" />
            </div>
          </SheetBody>
          <SheetFooter>
            <SheetClose asChild><Button variant="outline">Cancelar</Button></SheetClose>
            <Button onClick={() => create.mutate()} disabled={!name || create.isPending}>
              {create.isPending ? "Criando..." : "Gerar chave"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ─── Webhooks ──────────────────────────────────────────────────────────────

function WebhooksSection() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<{ name: string; url: string; events: string[] }>({ name: "", url: "", events: [] });
  const [newWh, setNewWh] = useState<WebhookCreated | null>(null);

  const list = useQuery({ queryKey: ["webhooks"], queryFn: () => webhooksApi.list() });

  const create = useMutation({
    mutationFn: () => webhooksApi.create(form),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["webhooks"] });
      setNewWh(data);
      setForm({ name: "", url: "", events: [] });
      setOpen(false);
      toast.success("Webhook criado");
    },
    onError: (e: Error) => toast.error("Falha", { description: e.message }),
  });

  const del = useMutation({
    mutationFn: (id: string) => webhooksApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["webhooks"] });
      toast.success("Removido");
    },
  });

  const test = useMutation({
    mutationFn: (id: string) => webhooksApi.test(id),
    onSuccess: (data) => {
      if (data.ok) toast.success(`Ping OK (${data.status})`);
      else toast.error("Falha no ping", { description: data.error ?? `Status ${data.status}` });
      qc.invalidateQueries({ queryKey: ["webhooks"] });
    },
  });

  const toggleEvent = (ev: string) => {
    setForm((f) => ({
      ...f,
      events: f.events.includes(ev) ? f.events.filter((e) => e !== ev) : [...f.events, ev],
    }));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Endpoints HTTP que recebem POST quando eventos acontecem.
        </p>
        <Button onClick={() => setOpen(true)}>
          <Plus className="h-4 w-4" /> Novo webhook
        </Button>
      </div>

      {newWh && (
        <div className="rounded-2xl border-2 border-amber-300/60 bg-amber-50/40 p-5">
          <h4 className="font-semibold text-sm">Webhook criado — salve o secret</h4>
          <p className="text-xs text-muted-foreground mt-1">Use pra validar HMAC SHA-256 dos payloads.</p>
          <code className="block mt-2 rounded-lg bg-white dark:bg-black/30 border p-2.5 font-mono text-xs break-all">
            {newWh.secret}
          </code>
          <Button size="sm" variant="ghost" onClick={() => setNewWh(null)} className="mt-3">Fechar</Button>
        </div>
      )}

      {list.data?.items.length === 0 ? (
        <EmptyState icon={Webhook} title="Sem webhooks" description="Conecte um endpoint pra receber eventos em tempo real." />
      ) : (
        <ul className="space-y-2">
          {list.data?.items.map((w) => (
            <li key={w.id} className="rounded-2xl border border-slate-200/60 dark:border-white/10 bg-white/70 dark:bg-card/40 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-semibold text-sm">{w.name}</h4>
                    {w.is_active ? <Badge variant="success" dot size="sm">Ativo</Badge> : <Badge variant="muted" size="sm">Inativo</Badge>}
                    {w.failure_count > 0 && (
                      <Badge variant="warning" size="sm">{w.failure_count} falha(s)</Badge>
                    )}
                  </div>
                  <p className="font-mono text-xs text-muted-foreground truncate mt-1 flex items-center gap-1">
                    <Globe className="h-3 w-3" />
                    {w.url}
                  </p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {w.events.map((e) => (
                      <Badge key={e} variant="default" size="sm">{e}</Badge>
                    ))}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-2 flex gap-3">
                    {w.last_success_at && (
                      <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-emerald-600" /> Sucesso: {formatRelative(w.last_success_at)}</span>
                    )}
                    {w.last_failure_at && (
                      <span className="inline-flex items-center gap-1"><XCircle className="h-3 w-3 text-rose-600" /> Falha: {formatRelative(w.last_failure_at)}</span>
                    )}
                  </div>
                </div>
                <div className="shrink-0 flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => test.mutate(w.id)}
                    disabled={test.isPending && test.variables === w.id}
                  >
                    {test.isPending && test.variables === w.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                    Ping
                  </Button>
                  <Button variant="ghost" size="icon-sm" onClick={() => del.mutate(w.id)} className="text-rose-600">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent size="lg">
          <SheetHeader>
            <SheetTitle>Novo webhook</SheetTitle>
          </SheetHeader>
          <SheetBody className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Nome</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Slack #ops-alerts" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">URL (HTTPS)</Label>
              <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://..." />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Eventos</Label>
              <div className="grid grid-cols-2 gap-2">
                {WEBHOOK_EVENTS.map((ev) => (
                  <label key={ev} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.events.includes(ev)}
                      onChange={() => toggleEvent(ev)}
                      className="rounded border-slate-300"
                    />
                    <code className="text-xs">{ev}</code>
                  </label>
                ))}
              </div>
            </div>
          </SheetBody>
          <SheetFooter>
            <SheetClose asChild><Button variant="outline">Cancelar</Button></SheetClose>
            <Button
              onClick={() => create.mutate()}
              disabled={!form.name || !form.url || create.isPending}
            >
              {create.isPending ? "Criando..." : "Criar webhook"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}

// suppress unused
void formatDateTime;
